import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { fetchAIConfig, isMockMode } from '../../../services/stockai/api/client';

const PRESETS = [
  { id: 'aggressive', label: '공격형', description: '높은 매수 빈도와 적극적인 진입을 선호합니다.', allocation: 70 },
  { id: 'balanced', label: '균형형', description: '리스크와 진입 빈도의 균형을 유지합니다.', allocation: 45 },
  { id: 'conservative', label: '보수형', description: '확신이 높은 종목만 선별해 진입합니다.', allocation: 25 },
];

const CHANNELS = [
  { id: 'email', label: '이메일', helper: '요약 리포트와 중요 체결 상태를 수신합니다.' },
  { id: 'telegram', label: '텔레그램', helper: '급등락/즉시 체결 알림에 적합합니다.' },
  { id: 'sms', label: 'SMS', helper: '긴급 경고와 예외 상황을 짧게 받습니다.' },
];

export default function Settings() {
  const { loggedIn, mock, openLogin, authTick } = useOutletContext() || {};
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [preset, setPreset] = useState('balanced');
  const [notifications, setNotifications] = useState({
    email: true,
    telegram: false,
    sms: false,
    digest: true,
    volatility: true,
    filled: true,
  });
  const [execution, setExecution] = useState({
    autoTrade: true,
    autoPauseOnLoss: true,
    requireManualApproval: !mock,
    dailyLimit: 3,
  });

  useEffect(() => {
    if (!mock && !loggedIn) {
      setConfig(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    fetchAIConfig()
      .then((data) => {
        if (cancelled) return;
        const next = data?.config || data || null;
        setConfig(next);
      })
      .catch((err) => {
        if (!cancelled) console.warn('[StockAI] 설정 로드 실패:', err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [loggedIn, mock, authTick]);

  const currentPreset = useMemo(
    () => PRESETS.find((item) => item.id === preset) || PRESETS[1],
    [preset]
  );

  if (loading) return <p className="stockai-card">로딩 중...</p>;

  return (
    <div>
      {!mock && !loggedIn && (
        <div className="stockai-alert">
          상세 설정은 로그인 후 실 API 정보와 함께 확인할 수 있습니다.{' '}
          <button type="button" className="stockai-btn stockai-btn--sm" onClick={openLogin}>로그인</button>
        </div>
      )}

      <div className="stockai-kicker">Operating Settings</div>
      <section className="stockai-hero">
        <div className="stockai-hero__panel">
          <h1 className="stockai-hero__headline">AI 설정, 알림, 성향 제어를 한 화면에</h1>
          <p className="stockai-hero__lead">
            현재 화면은 AI 매매 운영에 필요한 핵심 제어값을 묶은 상세 설정 콘솔입니다.
            추천 기준, 알림 채널, 구매 성향, 실행 컨트롤을 함께 조정할 수 있습니다.
          </p>
          <div className="stockai-chip-list">
            <span className="stockai-chip">모드 {isMockMode() ? 'Mock' : 'Live'}</span>
            <span className="stockai-chip">연결 {loggedIn || mock ? 'Ready' : 'Pending'}</span>
            <span className="stockai-chip">성향 {currentPreset.label}</span>
          </div>
        </div>

        <div className="stockai-hero__aside">
          <div className="stockai-card stockai-stat-card">
            <div className="stockai-metric">
              <span className="stockai-metric__eyebrow">자동매수</span>
              <strong className="stockai-metric__value">{config?.auto_buy?.enabled ? 'ON' : 'OFF'}</strong>
              <span className="stockai-metric__meta">
                매수 신뢰도 기준 {config?.auto_buy?.confidence_threshold ?? 70}%
              </span>
            </div>
          </div>
          <div className="stockai-card stockai-stat-card">
            <div className="stockai-metric">
              <span className="stockai-metric__eyebrow">실행 정책</span>
              <strong className="stockai-metric__value">{execution.requireManualApproval ? 'Human Check' : 'Auto Run'}</strong>
              <span className="stockai-metric__meta">일일 주문 제한 {execution.dailyLimit}회</span>
            </div>
          </div>
        </div>
      </section>

      <section className="stockai-section">
        <div className="stockai-section__header">
          <div>
            <h2 className="stockai-section__title">AI 설정</h2>
            <div className="stockai-section__subtitle">추천과 자동매매 흐름에 직접 영향을 주는 핵심 설정입니다.</div>
          </div>
        </div>
        <div className="stockai-grid stockai-grid--2">
          <div className="stockai-card">
            <div className="stockai-setting-row">
              <div>
                <strong>자동매수</strong>
                <div className="stockai-stat-label">조건 충족 종목을 자동으로 매수 후보에 올립니다.</div>
              </div>
              <button
                type="button"
                className={`stockai-toggle ${config?.auto_buy?.enabled ? 'stockai-toggle--active' : ''}`}
                onClick={() => setConfig((prev) => ({
                  ...(prev || {}),
                  auto_buy: {
                    ...(prev?.auto_buy || {}),
                    enabled: !prev?.auto_buy?.enabled,
                  },
                }))}
              >
                {config?.auto_buy?.enabled ? 'ON' : 'OFF'}
              </button>
            </div>
            <label className="stockai-form-label">매수 신뢰도 기준</label>
            <input
              className="stockai-range"
              type="range"
              min="50"
              max="95"
              value={config?.auto_buy?.confidence_threshold ?? 70}
              onChange={(e) => setConfig((prev) => ({
                ...(prev || {}),
                auto_buy: {
                  ...(prev?.auto_buy || {}),
                  confidence_threshold: Number(e.target.value),
                },
              }))}
            />
            <div className="stockai-stat-label">{config?.auto_buy?.confidence_threshold ?? 70}% 이상일 때 진입</div>
          </div>

          <div className="stockai-card">
            <div className="stockai-setting-row">
              <div>
                <strong>자동매도</strong>
                <div className="stockai-stat-label">목표 수익률/손절 조건을 만족하면 자동 청산 후보로 분류합니다.</div>
              </div>
              <button
                type="button"
                className={`stockai-toggle ${config?.auto_sell?.enabled ? 'stockai-toggle--active' : ''}`}
                onClick={() => setConfig((prev) => ({
                  ...(prev || {}),
                  auto_sell: {
                    ...(prev?.auto_sell || {}),
                    enabled: !prev?.auto_sell?.enabled,
                  },
                }))}
              >
                {config?.auto_sell?.enabled ? 'ON' : 'OFF'}
              </button>
            </div>

            <label className="stockai-form-label">전략 모드</label>
            <div className="stockai-chip-list">
              <button type="button" className="stockai-chip stockai-chip--button">Momentum</button>
              <button type="button" className="stockai-chip stockai-chip--button">Swing</button>
              <button type="button" className="stockai-chip stockai-chip--button">Risk Off</button>
            </div>
          </div>
        </div>
      </section>

      <section className="stockai-section">
        <div className="stockai-section__header">
          <div>
            <h2 className="stockai-section__title">알림 설정</h2>
            <div className="stockai-section__subtitle">채널별 수신 방식을 정리해 체결/리스크/요약 정보를 분리합니다.</div>
          </div>
        </div>
        <div className="stockai-grid stockai-grid--3">
          {CHANNELS.map((channel) => (
            <div key={channel.id} className="stockai-card stockai-feature-card">
              <div className="stockai-setting-row">
                <strong>{channel.label}</strong>
                <button
                  type="button"
                  className={`stockai-toggle ${notifications[channel.id] ? 'stockai-toggle--active' : ''}`}
                  onClick={() => setNotifications((prev) => ({ ...prev, [channel.id]: !prev[channel.id] }))}
                >
                  {notifications[channel.id] ? 'ON' : 'OFF'}
                </button>
              </div>
              <p className="stockai-stat-label">{channel.helper}</p>
            </div>
          ))}
        </div>
        <div className="stockai-grid stockai-grid--3 stockai-section">
          <div className="stockai-card">
            <div className="stockai-setting-row">
              <span>일간 요약 리포트</span>
              <button
                type="button"
                className={`stockai-toggle ${notifications.digest ? 'stockai-toggle--active' : ''}`}
                onClick={() => setNotifications((prev) => ({ ...prev, digest: !prev.digest }))}
              >
                {notifications.digest ? 'ON' : 'OFF'}
              </button>
            </div>
          </div>
          <div className="stockai-card">
            <div className="stockai-setting-row">
              <span>변동성 경고</span>
              <button
                type="button"
                className={`stockai-toggle ${notifications.volatility ? 'stockai-toggle--active' : ''}`}
                onClick={() => setNotifications((prev) => ({ ...prev, volatility: !prev.volatility }))}
              >
                {notifications.volatility ? 'ON' : 'OFF'}
              </button>
            </div>
          </div>
          <div className="stockai-card">
            <div className="stockai-setting-row">
              <span>체결 완료 알림</span>
              <button
                type="button"
                className={`stockai-toggle ${notifications.filled ? 'stockai-toggle--active' : ''}`}
                onClick={() => setNotifications((prev) => ({ ...prev, filled: !prev.filled }))}
              >
                {notifications.filled ? 'ON' : 'OFF'}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="stockai-section">
        <div className="stockai-section__header">
          <div>
            <h2 className="stockai-section__title">구매 성향</h2>
            <div className="stockai-section__subtitle">AI 추천을 어떤 톤으로 받아들일지 프리셋으로 정의합니다.</div>
          </div>
        </div>
        <div className="stockai-grid stockai-grid--3">
          {PRESETS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`stockai-card stockai-preset-card ${preset === item.id ? 'stockai-preset-card--active' : ''}`}
              onClick={() => setPreset(item.id)}
            >
              <div className="stockai-setting-row">
                <strong>{item.label}</strong>
                <span className="stockai-badge">비중 {item.allocation}%</span>
              </div>
              <p className="stockai-stat-label">{item.description}</p>
            </button>
          ))}
        </div>
      </section>

      <section className="stockai-section">
        <div className="stockai-section__header">
          <div>
            <h2 className="stockai-section__title">실행 컨트롤</h2>
            <div className="stockai-section__subtitle">실거래 안전장치와 자동 실행 조건을 함께 관리합니다.</div>
          </div>
        </div>
        <div className="stockai-grid stockai-grid--2">
          <div className="stockai-card">
            <div className="stockai-setting-row">
              <div>
                <strong>자동매매 실행</strong>
                <div className="stockai-stat-label">추천이 조건을 만족하면 실행 파이프라인을 활성화합니다.</div>
              </div>
              <button
                type="button"
                className={`stockai-toggle ${execution.autoTrade ? 'stockai-toggle--active' : ''}`}
                onClick={() => setExecution((prev) => ({ ...prev, autoTrade: !prev.autoTrade }))}
              >
                {execution.autoTrade ? 'ON' : 'OFF'}
              </button>
            </div>
            <div className="stockai-setting-row">
              <div>
                <strong>손실 확대 시 자동 일시정지</strong>
                <div className="stockai-stat-label">예상치 못한 급변 시 자동으로 실행을 멈춥니다.</div>
              </div>
              <button
                type="button"
                className={`stockai-toggle ${execution.autoPauseOnLoss ? 'stockai-toggle--active' : ''}`}
                onClick={() => setExecution((prev) => ({ ...prev, autoPauseOnLoss: !prev.autoPauseOnLoss }))}
              >
                {execution.autoPauseOnLoss ? 'ON' : 'OFF'}
              </button>
            </div>
          </div>

          <div className="stockai-card">
            <div className="stockai-setting-row">
              <div>
                <strong>최종 주문 전 수동 승인</strong>
                <div className="stockai-stat-label">실 API 모드에서는 마지막 단계에서 사용자 승인을 요구합니다.</div>
              </div>
              <button
                type="button"
                className={`stockai-toggle ${execution.requireManualApproval ? 'stockai-toggle--active' : ''}`}
                onClick={() => setExecution((prev) => ({ ...prev, requireManualApproval: !prev.requireManualApproval }))}
              >
                {execution.requireManualApproval ? 'ON' : 'OFF'}
              </button>
            </div>
            <label className="stockai-form-label">일일 주문 제한</label>
            <input
              className="stockai-range"
              type="range"
              min="1"
              max="10"
              value={execution.dailyLimit}
              onChange={(e) => setExecution((prev) => ({ ...prev, dailyLimit: Number(e.target.value) }))}
            />
            <div className="stockai-stat-label">하루 최대 {execution.dailyLimit}회 실행</div>
          </div>
        </div>
      </section>
    </div>
  );
}
